import React from 'react';
import Login from './Login';
import TripPage from './TripPage';
import ItineraryPage from './ItineraryPage';
import ItineraryPageV2 from './ItineraryPageV2';
import LayoutWrapper from './LayoutWrapper';
import ViewTripItinerary from './ViewTripItinerary';
import EditTripPage from './EditTripPage'; // New import
import ArchivedTripsPage from './ArchivedTripsPage';
import Calendar from './Calendar';
import SharingPage from './SharingPage';
import FlightSearchPage from './FlightSearchPage';
import DayManagement from './DayManagement';
import ExpensesPage from './ExpensesPage';
import StandaloneExpensesPage from './StandaloneExpensesPage';
import ExpenseReportsPage from './ExpenseReportsPage';
import ExpenseReportDetailPage from './ExpenseReportDetailPage';
import AdminConsole from './AdminConsole';
import MemoriesPage from './MemoriesPage';
import HamburgerMenu from './HamburgerMenu';
import AIIcon from './components/AIIcon';
import PlaneEarthIcon from './components/PlaneEarthIcon';
import Logo from './components/Logo';
import SettingsMenu from './components/SettingsMenu';
import MessageCenter from './components/MessageCenter';
import GuestBanner from './components/GuestBanner';
import AccountLinking from './components/AccountLinking';
import ProfileSettings from './components/ProfileSettings';
import AiMessage from './AiMessage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import HelpPage from './HelpPage';
import './AiMessage.css';
import './App.css';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Toolbar, Typography, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'; // New imports for AppBar, Toolbar, Typography, Box, IconButton
import HomeIcon from '@mui/icons-material/Home';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from './config';
import theme from './theme';

const AppContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Extract tripId from URL if on a trip page (e.g., /trip/123 or /trip/123/day/2024-01-01)
    const currentTripId = React.useMemo(() => {
        const match = location.pathname.match(/\/trip\/(\d+)/);
        return match ? match[1] : null;
    }, [location.pathname]);
    
    const [isLoggedIn, setIsLoggedIn] = React.useState(false);
    const [user, setUser] = React.useState(null);
    const [isAuthLoading, setIsAuthLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [isAiWorking, setIsAiWorking] = React.useState(false);
    const [showAiMessage, setShowAiMessage] = React.useState(false);
    const [aiMessage, setAiMessage] = React.useState('');
    const [showCenteredOverlay, setShowCenteredOverlay] = React.useState(false);
    const [showAccountLinking, setShowAccountLinking] = React.useState(false);
    const [showProfileSettings, setShowProfileSettings] = React.useState(false);
    const [showEmailReminder, setShowEmailReminder] = React.useState(false);

    // Prompt the user to add an email address (needed so forwarded emails can be
    // routed to their account) on every login and, if still missing, once per day.
    React.useEffect(() => {
        if (!isLoggedIn || !user) return;
        const hasEmail = !!(user.email && String(user.email).trim());
        if (hasEmail || user.is_guest) {
            setShowEmailReminder(false);
            return;
        }
        const today = new Date().toISOString().slice(0, 10);
        const lastShown = localStorage.getItem('emailReminderLastShown');
        if (lastShown !== today) {
            setShowEmailReminder(true);
            localStorage.setItem('emailReminderLastShown', today);
        }
    }, [isLoggedIn, user]);

    // Effect to handle the 3-second timer for centered overlay
    React.useEffect(() => {
        if (isAiWorking) {
            setShowCenteredOverlay(true);
            const timer = setTimeout(() => {
                setShowCenteredOverlay(false);
            }, 3000);
            return () => clearTimeout(timer);
        } else {
            setShowCenteredOverlay(false);
        }
    }, [isAiWorking]);

        const handleShowAiMessage = (message) => {
        setAiMessage(message);
        setShowAiMessage(true);
    };

    React.useEffect(() => {
        const verifyLogin = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            let token = urlParams.get('token');

            if (token) {
                localStorage.setItem('token', token);
                window.history.replaceState({}, document.title, window.location.pathname);
            } else {
                token = localStorage.getItem('token');
            }

            if (token) {
                try {
                    const response = await fetch(`${API_BASE_URL}/me.php`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (response.ok) {
                        const userData = await response.json();
                        // Migrate guest trips if a guest_token is stored (handles WhatsApp login case)
                        if (!userData.is_guest) {
                            const guestToken = localStorage.getItem('guest_token');
                            if (guestToken) {
                                try {
                                    await fetch(`${API_BASE_URL}/migrate_guest.php`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ guest_token: guestToken })
                                    });
                                    localStorage.removeItem('guest_token');
                                } catch (e) { /* silent */ }
                            }
                        }
                        setIsLoggedIn(true);
                        setIsAuthLoading(false);
                        setUser(userData);
                        if (userData?.pu_id) localStorage.setItem('pu_id', userData.pu_id);
                        // Cache the profile so the session survives offline reloads.
                        try { localStorage.setItem('cached_user', JSON.stringify(userData)); } catch (_) {}
                    } else {
                        // Server reachable but rejected the token: genuinely log out.
                        localStorage.removeItem('token');
                        localStorage.removeItem('cached_user');
                        setIsLoggedIn(false);
                        setUser(null);
                        setIsAuthLoading(false);
                    }
                } catch (error) {
                    // Network failure (e.g. offline): keep the session using the cached
                    // profile instead of logging the user out, so cached trips stay usable.
                    console.error('Verification request failed:', error);
                    let restored = false;
                    try {
                        const cachedRaw = localStorage.getItem('cached_user');
                        if (cachedRaw) {
                            setUser(JSON.parse(cachedRaw));
                            setIsLoggedIn(true);
                            restored = true;
                        }
                    } catch (_) {}
                    if (!restored) {
                        setIsLoggedIn(false);
                        setUser(null);
                    }
                    setIsAuthLoading(false);
                }
            } else {
                setIsAuthLoading(false);
            }
        };
        
        // Initialize OAuth SDKs
        const initializeOAuth = () => {
            // Initialize Facebook SDK
            if (window.FB && process.env.REACT_APP_FACEBOOK_APP_ID) {
                window.FB.init({
                    appId: process.env.REACT_APP_FACEBOOK_APP_ID,
                    cookie: true,
                    xfbml: true,
                    version: 'v18.0'
                });
            }
        };
        
        verifyLogin();
        initializeOAuth();
    }, []);

    // Dev/testing helper: force AI Indicator on via URL param `?force_ai=1`
    React.useEffect(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('force_ai') === '1') {
                setIsAiWorking(true);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    const migrateGuestTrips = async (authToken) => {
        const guestToken = localStorage.getItem('guest_token');
        if (!guestToken) return;
        try {
            await fetch(`${API_BASE_URL}/migrate_guest.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ guest_token: guestToken })
            });
            localStorage.removeItem('guest_token');
        } catch (err) {
            console.error('Guest migration failed:', err);
        }
    };

    const handleEmailLogin = async (username, password) => {
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/email_password_login.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await response.json();
            if (response.ok && data.token) {
                localStorage.setItem('token', data.token);
                await migrateGuestTrips(data.token);
                const meResp = await fetch(`${API_BASE_URL}/me.php`, { headers: { 'Authorization': `Bearer ${data.token}` } });
                const meData = await meResp.json();
                setUser(meResp.ok && meData.user_id ? meData : data.user);
                setIsLoggedIn(true);
                setIsAuthLoading(false);
                return { success: true };
            } else {
                setError(data.message || 'Login failed');
                return { success: false };
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Failed to connect to server');
            return { success: false };
        }
    };

    const handleEmailRegister = async (username, password, name) => {
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/email_password_register.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, name })
            });
            const data = await response.json();
            if (response.ok && data.token) {
                localStorage.setItem('token', data.token);
                await migrateGuestTrips(data.token);
                const meResp = await fetch(`${API_BASE_URL}/me.php`, { headers: { 'Authorization': `Bearer ${data.token}` } });
                const meData = await meResp.json();
                setUser(meResp.ok && meData.user_id ? meData : data.user);
                setIsLoggedIn(true);
                setIsAuthLoading(false);
                return { success: true };
            } else {
                setError(data.message || 'Registration failed');
                return { success: false };
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('Failed to connect to server');
            return { success: false };
        }
    };

    const handleGuestLogin = async () => {
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/guest_login.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            const data = await response.json();
            
            if (response.ok && data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('guest_token', data.user.guest_token);
                setUser(data.user);
                setIsLoggedIn(true);
                setIsAuthLoading(false);
                return { success: true };
            } else {
                setError(data.message || 'Failed to continue as guest');
                return { success: false };
            }
        } catch (error) {
            console.error('Guest login error:', error);
            setError('Failed to connect to server');
            return { success: false };
        }
    };

    const handleWhatsAppLogin = async (token) => {
        setError('');
        try {
            localStorage.setItem('token', token);
            await migrateGuestTrips(token);
            const meResp = await fetch(`${API_BASE_URL}/me.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const meData = await meResp.json();
            if (meResp.ok && meData.user_id) {
                setUser(meData);
                setIsLoggedIn(true);
                setIsAuthLoading(false);
            } else {
                setError('Login failed. Please try again.');
            }
        } catch (err) {
            setError('Failed to connect to the server for login.');
        }
    };

    const handleGoogleLogin = () => {
        setError('');
        
        // Check if Google Client ID is configured
        if (!process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
            setError('Google login is not configured. Please contact support.');
            return;
        }
        
        // Check if Google API is loaded
        if (!window.gapi) {
            setError('Google API not loaded. Please refresh the page and try again.');
            return;
        }
        
        // Initialize Google OAuth
        window.gapi.load('auth2', function() {
            const auth2 = window.gapi.auth2.init({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID
            });
            
            auth2.signIn().then(function(googleUser) {
                const profile = googleUser.getBasicProfile();
                const idToken = googleUser.getAuthResponse().id_token;
                
                // Send to your backend for verification
                fetch(`${API_BASE_URL}/google_login.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        id_token: idToken,
                        email: profile.getEmail(),
                        name: profile.getName()
                    })
                })
                .then(response => response.json())
                .then(data => {
                    if (data.token) {
                        localStorage.setItem('token', data.token);
                        setUser(data.user);
                        setIsLoggedIn(true);
                        setIsAuthLoading(false);
                    } else {
                        setError(data.message || 'Google login failed');
                    }
                })
                .catch(err => {
                    console.error('Google login error:', err);
                    setError('Failed to authenticate with Google');
                });
            }).catch(function(error) {
                console.error('Google sign-in error:', error);
                setError('Google login was cancelled or failed');
            });
        });
    };

    const handleFacebookLogin = () => {
        setError('');
        
        // Check if Facebook App ID is configured
        if (!process.env.REACT_APP_FACEBOOK_APP_ID || process.env.REACT_APP_FACEBOOK_APP_ID === 'YOUR_FACEBOOK_APP_ID_HERE') {
            setError('Facebook login is not configured. Please contact support.');
            return;
        }
        
        // Check if Facebook SDK is loaded
        if (!window.FB) {
            setError('Facebook SDK not loaded. Please refresh the page and try again.');
            return;
        }
        
        window.FB.login(function(response) {
            if (response.authResponse) {
                window.FB.api('/me', {fields: 'name,email'}, function(profile) {
                    // Send to your backend for verification
                    fetch(`${API_BASE_URL}/facebook_login.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            access_token: response.authResponse.accessToken,
                            user_id: response.authResponse.userID,
                            email: profile.email,
                            name: profile.name
                        })
                    })
                    .then(response => response.json())
                    .then(data => {
                        if (data.token) {
                            localStorage.setItem('token', data.token);
                            setUser(data.user);
                            setIsLoggedIn(true);
                        setIsAuthLoading(false);
                        } else {
                            setError(data.message || 'Facebook login failed');
                        }
                    })
                    .catch(err => {
                        console.error('Facebook login error:', err);
                        setError('Failed to authenticate with Facebook');
                    });
                });
            } else {
                setError('Facebook login was cancelled');
            }
        }, {scope: 'email'});
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('cached_user');
        // Clear the daily throttle so the "add your email" reminder shows again
        // on the next login.
        localStorage.removeItem('emailReminderLastShown');
        setIsLoggedIn(false);
        setUser(null);
                        setIsAuthLoading(false);
    };

    const handleAccountLinked = (updatedUser) => {
        setUser(updatedUser);
        setShowAccountLinking(false);
    };

    
    try {
        return (
            <LayoutWrapper>
            <div className="tw-flex tw-flex-col tw-min-h-screen">

                {/* ── Top nav bar ── */}
                <header className="tw-sticky tw-top-0 tw-z-40 tw-bg-surface-glass tw-backdrop-blur-md tw-border-b tw-border-surface-border tw-flex-shrink-0">
                    <div className="tw-max-w-screen-lg tw-mx-auto tw-flex tw-items-center tw-px-4 sm:tw-px-6 tw-h-16 sm:tw-h-[72px] tw-gap-4">
                        {/* Logo */}
                        <Logo
                            onClick={() => navigate('/')}
                            size="medium"
                            showSubtitle={true}
                        />

                        {/* Spacer */}
                        <div className="tw-flex-1" />

                        {/* Right actions */}
                        <div className="tw-flex tw-items-center tw-gap-5 tw-flex-shrink-0">
                            <SettingsMenu />
                            {isLoggedIn && (
                                <MessageCenter isLoggedIn={isLoggedIn} />
                            )}
                            {isLoggedIn && (
                                <button
                                    onClick={() => navigate('/')}
                                    title={t('go_to_trip_page')}
                                    className="tw-p-1 tw-rounded-xl tw-text-white/80 hover:tw-text-white hover:tw-bg-white/10 tw-transition-all tw-duration-200"
                                >
                                    <PlaneEarthIcon sx={{ fontSize: 22 }} />
                                </button>
                            )}
                            <HamburgerMenu
                                isLoggedIn={isLoggedIn}
                                user={user}
                                onLogout={handleLogout}
                                onShowAiMessage={handleShowAiMessage}
                                setIsAiWorking={setIsAiWorking}
                                onShowAccountLinking={() => setShowAccountLinking(true)}
                                onShowProfileSettings={() => setShowProfileSettings(true)}
                                currentTripId={currentTripId}
                                onShareTrip={(tripId) => navigate(`/sharing?trip=${tripId}`)}
                            />
                        </div>
                    </div>
                </header>

                {/* ── Main content ── */}
                <main className="tw-flex-1 tw-overflow-auto">
                            {isAuthLoading ? (
                                <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
                                    <span className="tw-text-sm tw-text-muted">{t('loading')}</span>
                                </div>
                            ) : isLoggedIn ? (
                                <>
                                    <GuestBanner 
                                        user={user} 
                                        onConvertAccount={() => {
                                            handleLogout();
                                        }} 
                                    />
                                    <AccountLinking
                                        open={showAccountLinking}
                                        onClose={() => setShowAccountLinking(false)}
                                        user={user}
                                        onAccountLinked={handleAccountLinked}
                                    />
                                    <ProfileSettings
                                        open={showProfileSettings}
                                        onClose={() => setShowProfileSettings(false)}
                                        user={user}
                                        onProfileUpdated={(updatedUser) => setUser(updatedUser)}
                                    />
                                    <Dialog
                                        open={showEmailReminder}
                                        onClose={() => setShowEmailReminder(false)}
                                        maxWidth="xs"
                                        fullWidth
                                    >
                                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MarkEmailReadOutlinedIcon color="primary" />
                                            {t('add_email_reminder_title') !== 'add_email_reminder_title'
                                                ? t('add_email_reminder_title')
                                                : 'Add your email address'}
                                        </DialogTitle>
                                        <DialogContent>
                                            <Typography variant="body2" color="text.secondary">
                                                Add your email address so forwarded emails will be routed to your account.
                                            </Typography>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setShowEmailReminder(false)}>
                                                {t('later') !== 'later' ? t('later') : 'Later'}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    setShowEmailReminder(false);
                                                    setShowProfileSettings(true);
                                                }}
                                            >
                                                {t('add_email') !== 'add_email' ? t('add_email') : 'Add email'}
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Routes>
                                        <Route path="/" element={<TripPage user={user} setIsAiWorking={setIsAiWorking} />} />
                                        <Route path="/trip/:tripId" element={<ItineraryPageV2 user={user} isAiWorking={isAiWorking} setIsAiWorking={setIsAiWorking} handleShowAiMessage={handleShowAiMessage} />} />
                                        <Route path="/trip/:tripId/expenses" element={<ExpensesPage user={user} />} />
                                        <Route path="/expenses" element={<ExpenseReportsPage user={user} />} />
                                        <Route path="/expenses/:reportId" element={<ExpenseReportDetailPage user={user} />} />
                                        <Route path="/trip/:tripId/edit" element={<EditTripPage setIsAiWorking={setIsAiWorking} />} />
                                        <Route path="/trip/:tripId/day/:dayDate" element={<DayManagement isAiWorking={isAiWorking} setIsAiWorking={setIsAiWorking} />} />
                                        <Route path="/trip/:tripId/memories" element={<MemoriesPage />} />
                                        <Route path="/view-trip/:tripId" element={<ViewTripItinerary />} />
                                    <Route path="/itinerary" element={<ItineraryPage user={user} isAiWorking={isAiWorking} setIsAiWorking={setIsAiWorking} handleShowAiMessage={handleShowAiMessage} />} />
                                    <Route path="/edit-trip" element={<EditTripPage setIsAiWorking={setIsAiWorking} />} />
                                    <Route path="/archived" element={<ArchivedTripsPage />} />
                                    <Route path="/calendar" element={<Calendar />} />
                                    <Route path="/sharing" element={<SharingPage />} />
                                        <Route path="/flight-search" element={<FlightSearchPage />} />
                                        <Route path="/admin" element={<AdminConsole />} />
                                        <Route path="/help" element={<HelpPage />} />
                                    </Routes>
                                </>
                            ) : (
                                <Routes>
                                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                                    <Route path="/help" element={<HelpPage />} />
                                    <Route path="*" element={
                                        <Login 
                                            onWhatsAppLogin={handleWhatsAppLogin}
                                            onEmailLogin={handleEmailLogin}
                                            onEmailRegister={handleEmailRegister}
                                            onGuestLogin={handleGuestLogin}
                                            onForgotPassword={(email) => navigate('/forgot-password', { state: { email } })}
                                            error={error} 
                                        />
                                    } />
                                </Routes>
                            )}
                </main>

                {/* ── AI working indicator ── */}
                {isAiWorking && (
                    <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none tw-z-[9999]">
                        <div className="ai-floating-indicator tw-flex tw-items-center tw-gap-3 tw-rounded-full tw-px-5 tw-py-3">
                            <AIIcon
                                className="ai-spin-slow"
                                sx={{ fontSize: 28, color: '#2ecc71' }}
                            />
                            <span className="ai-text-blink tw-text-emerald-400 tw-font-bold tw-whitespace-nowrap tw-text-base">
                                {t('ai_agent_in_action')}
                            </span>
                            <div className="tw-flex tw-gap-1 tw-ml-1">
                                <div className="loading-dot dot-1 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-400" />
                                <div className="loading-dot dot-2 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-400" />
                                <div className="loading-dot dot-3 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-400" />
                            </div>
                        </div>
                    </div>
                        )}
                    </div>
            </LayoutWrapper>
        );
    } catch (error) {
        console.error('🚨 APP.JSX: Error during render:', error);
        return <div>App Render Error: {error.message}</div>;
    }
};

const App = () => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter basename="/travel2">
                <AppContent />
            </BrowserRouter>
        </ThemeProvider>
    );
};

export default App;
